// TPLA-209: rvs voor de onderdorpel. Alle onderdorpels delen hetzelfde materiaal.
// Het materiaal gebruikt een echte texture-set (Metal041A) en een HDRI voor de reflecties. De getekende omgeving
// hieronder dient alleen als terugval zolang de HDRI nog laadt; de getekende rvs-texture en borstelstructuur
// worden nu niet gebruikt maar blijven bewaard.
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'

let rvsMaterial = null

// equirectangulaire omgeving: lichte lucht, felle softbox-band op de horizon en een donkere vloer,
// dat geeft de schuine lichtstreep die je op geborsteld rvs ziet
function maakOmgeving (THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 256
  const ctx = canvas.getContext('2d')

  const achtergrond = ctx.createLinearGradient(0, 0, 0, canvas.height)
  // middengrijs met een rustig verloop: genoeg verschil voor glans, maar nergens wit of zwart
  achtergrond.addColorStop(0, '#b8b8b8')
  achtergrond.addColorStop(0.35, '#999999')
  achtergrond.addColorStop(0.47, '#8a8a8a')
  achtergrond.addColorStop(0.53, '#808080')
  achtergrond.addColorStop(0.7, '#707070')
  achtergrond.addColorStop(1, '#646464')
  ctx.fillStyle = achtergrond
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // softboxen: zachte, felle lichtvlakken die als lichtstrepen in het metaal terugkomen
  const softbox = (x, y, breedte, hoogte) => {
    const licht = ctx.createLinearGradient(0, y - hoogte / 2, 0, y + hoogte / 2)
    licht.addColorStop(0, 'rgba(255,255,255,0)')
    licht.addColorStop(0.5, 'rgba(255,255,255,0.55)')
    licht.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = licht
    ctx.fillRect(x, y - hoogte / 2, breedte, hoogte)
  }
  // zachte lichtband rond de horizon: die ziet de verticale voorkant van de dorpel
  softbox(0, 118, canvas.width, 30)
  softbox(40, 70, 160, 28)
  softbox(300, 80, 140, 24)

  const texture = new THREE.CanvasTexture(canvas)
  texture.mapping = THREE.EquirectangularReflectionMapping
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

// fijne lengtestrepen in de ruwheid, zodat de reflectie "geborsteld" uitgesmeerd wordt
function maakBorstelStructuur (THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 1024
  canvas.height = 128
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = '#808080'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  for (let i = 0; i < 1400; i++) {
    const grijs = Math.floor(90 + Math.random() * 90)
    ctx.strokeStyle = `rgba(${grijs},${grijs},${grijs},${0.25 + Math.random() * 0.4})`
    ctx.lineWidth = Math.random() < 0.85 ? 1 : 2
    const y = Math.random() * canvas.height
    const x = Math.random() * canvas.width
    const lengte = 150 + Math.random() * 600
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + lengte, y)
    ctx.stroke()
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.repeat.set(2, 1)
  texture.anisotropy = 16
  return texture
}

// kleurtexture van geborsteld rvs: lichtgrijs verloop, een schuine zachte lichtstreep en fijne
// borstelstrepen in de lengte. Zo ziet de dorpel er ook zonder sterke reflecties uit als rvs.
function maakRvsTexture (THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 1024
  canvas.height = 256
  const ctx = canvas.getContext('2d')

  // basis: van iets donkerder grijs naar lichter grijs
  const basis = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
  basis.addColorStop(0, '#9c9fa2')
  basis.addColorStop(0.5, '#c4c7ca')
  basis.addColorStop(1, '#8f9295')
  ctx.fillStyle = basis
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // schuine zachte lichtstreep
  ctx.save()
  ctx.translate(canvas.width * 0.45, canvas.height / 2)
  ctx.rotate(-0.12)
  const streep = ctx.createLinearGradient(0, -90, 0, 90)
  streep.addColorStop(0, 'rgba(255,255,255,0)')
  streep.addColorStop(0.5, 'rgba(255,255,255,0.45)')
  streep.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = streep
  ctx.fillRect(-canvas.width, -90, canvas.width * 2, 180)
  ctx.restore()

  // borstelstrepen: veel dunne lichte en donkere lijnen in de lengte
  for (let i = 0; i < 2600; i++) {
    const licht = Math.random() < 0.5
    const alpha = 0.04 + Math.random() * 0.1
    ctx.strokeStyle = licht ? `rgba(255,255,255,${alpha})` : `rgba(40,42,45,${alpha})`
    ctx.lineWidth = Math.random() < 0.9 ? 1 : 2
    const y = Math.random() * canvas.height
    const x = Math.random() * canvas.width - 200
    const lengte = 200 + Math.random() * 800
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + lengte, y)
    ctx.stroke()
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.anisotropy = 16
  return texture
}

// echte texture-set voor geborsteld metaal (Metal041A van ambientCG, staat in public/textures)
function laadMetaalTexture (THREE, bestand, isKleur) {
  const texture = new THREE.TextureLoader().load(`/textures/${bestand}`)
  // alleen de kleurkaart is sRGB; een ruwheidskaart bevat data en moet lineair blijven
  if (isKleur) texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.anisotropy = 16
  return texture
}

// HDRI voor de reflecties: studio_small_09 (1K) van Poly Haven, CC0, staat in public/textures.
// Alleen het rvs-materiaal gebruikt hem als envMap; de verlichting van de rest van de scene verandert niet.
const HDRI_BESTAND = 'studio_small_09_1k.hdr'

// Deze studio-HDRI heeft de lichtbakken aan één kant. De mooie, lichte kant is wat een vlak dat naar -z wijst
// weerspiegelt; in de equirectangulaire afbeelding is dat u = 0.25 (three: u = atan2(z, x) / 2pi + 0.5).
const HDRI_LICHTE_KANT_U = 0.25

// Breedte van de zachte overgang tussen twee kwarten, als deel van een volle rondte (0.04 ≈ 14 graden).
const HDRI_OVERGANG = 0.04

// Felle verticale lichtstroken (softboxen) die in elk kwart van de HDRI worden toegevoegd. Zonder herkenbare
// lichtbronnen is de reflectie bijna egaal en gaan de vlekken in de metaaltexture overheersen (marmer-effect);
// rvs herken je juist aan scherpe lichte reflecties naast donkere stukken.
//  - positie: plek binnen het kwart, t.o.v. het midden (-0.125 .. 0.125, deel van een volle rondte)
//  - breedte: breedte van de strook (deel van een volle rondte), met zachte randen
//  - van/tot: hoogte in de HDRI (0 = recht naar beneden, 0.5 = horizon, 1 = recht omhoog)
//  - sterkte: hoeveel licht erbij komt (HDR-waarde; de achtergrond ligt rond 1). Boven ~3 schiet de reflectie na
//    de ACES-tonemapping door naar wit en smeert de ruwheid dat wit breed uit, dus bewust bescheiden.
//  - kanteling: hoeveel de strook per hoogte opschuift (deel van een rondte per volle hoogte); een schuine strook
//    oogt als een lamp of raam in een ruimte, een kaarsrechte strook geeft een onnatuurlijke verticale baan
const HDRI_LICHTSTROKEN = [
  { positie: -0.065, breedte: 0.006, van: 0.35, tot: 0.8, sterkte: 2.2, kanteling: 0.05 },
  { positie: -0.05, breedte: 0.002, van: 0.4, tot: 0.72, sterkte: 1.4, kanteling: 0.05 },
  { positie: 0.035, breedte: 0.01, van: 0.58, tot: 0.88, sterkte: 1.8, kanteling: -0.04 },
  { positie: 0.085, breedte: 0.004, van: 0.22, tot: 0.5, sterkte: 1.2, kanteling: 0.03 }
]

function voegLichtstrokenToe (THREE, hdri, stroken) {
  const { width, height, data } = hdri.image
  if (!data) return
  const kanalen = data.length / (width * height)
  const isHalf = data instanceof Uint16Array
  const lees = isHalf ? (v) => THREE.DataUtils.fromHalfFloat(v) : (v) => v
  const schrijf = isHalf ? (v) => THREE.DataUtils.toHalfFloat(v) : (v) => v
  const smoothstep = (t) => { const c = Math.min(1, Math.max(0, t)); return c * c * (3 - 2 * c) }
  // zachte rand: 1 in de kern, aflopend tot 0 over 'rand' buiten [van, tot]
  const zacht = (waarde, van, tot, rand) => smoothstep((waarde - (van - rand)) / rand) * smoothstep(((tot + rand) - waarde) / rand)

  const kwart = 0.25
  for (let y = 0; y < height; y++) {
    // data-rij 0 is de bovenkant van het beeld; v loopt van onder (0) naar boven (1)
    const v = 1 - (y + 0.5) / height
    const rij = y * width * kanalen
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width
      const d = ((((u + kwart / 2) % kwart) + kwart) % kwart) - kwart / 2
      let extra = 0
      for (const strook of stroken) {
        const half = strook.breedte / 2
        // schuine strook: het midden schuift mee met de hoogte
        const midden = strook.positie + (strook.kanteling || 0) * (v - 0.5)
        const horizontaal = zacht(d, midden - half, midden + half, half * 0.6)
        if (horizontaal <= 0) continue
        extra += strook.sterkte * horizontaal * zacht(v, strook.van, strook.tot, 0.05)
      }
      if (extra <= 0) continue
      for (let k = 0; k < Math.min(3, kanalen); k++) {
        const i = rij + x * kanalen + k
        data[i] = schrijf(lees(data[i]) + extra)
      }
    }
  }
  hdri.needsUpdate = true
}

// Bouwt de HDRI om tot een omgeving die rondom gelijk is: het kwart rond de lichte kant wordt vier keer rondom
// gezet, gecentreerd op de vier richtingen waar vlakken naar kunnen wijzen (-x, -z, +x, +z), zodat elke kant van
// het gebouw dezelfde reflectie krijgt. Op de vier naden lopen de kwarten met een zachte overgang in elkaar over.
// De verticale opbouw (boven / horizon / onder) blijft die van de originele HDRI.
// three r158 kent nog geen envMapRotation, daarom wordt de afbeelding zelf aangepast.
function maakHdriRondom (THREE, hdri, lichteKantU, overgang) {
  const { width, height, data } = hdri.image
  if (!data) return
  const kanalen = data.length / (width * height)
  const isHalf = data instanceof Uint16Array
  const lees = isHalf ? (v) => THREE.DataUtils.fromHalfFloat(v) : (v) => v
  const schrijf = isHalf ? (v) => THREE.DataUtils.toHalfFloat(v) : (v) => v

  const kwart = 0.25
  const halveOvergang = overgang / 2
  const kolom = (u) => Math.floor((((u % 1) + 1) % 1) * width) % width
  const smoothstep = (t) => { const c = Math.min(1, Math.max(0, t)); return c * c * (3 - 2 * c) }

  // per uitvoerkolom: uit welke bronkolom(men) hij komt en met welk gewicht
  const bron = []
  for (let x = 0; x < width; x++) {
    const u = (x + 0.5) / width
    // positie t.o.v. het midden van het dichtstbijzijnde kwart, in [-1/8, 1/8)
    const d = ((((u + kwart / 2) % kwart) + kwart) % kwart) - kwart / 2
    let ander = null
    let gewicht = 0
    if (d > kwart / 2 - halveOvergang) {
      // vlak voor de naad naar het volgende kwart: meng met wat dat kwart hier zou tonen
      ander = lichteKantU + d - kwart
      gewicht = 0.5 * smoothstep((d - (kwart / 2 - halveOvergang)) / halveOvergang)
    } else if (d < -kwart / 2 + halveOvergang) {
      // net na de naad van het vorige kwart
      ander = lichteKantU + d + kwart
      gewicht = 0.5 * smoothstep(((-kwart / 2 + halveOvergang) - d) / halveOvergang)
    }
    bron.push({ eigen: kolom(lichteKantU + d), ander: ander === null ? null : kolom(ander), gewicht })
  }

  const nieuw = new data.constructor(data.length)
  for (let y = 0; y < height; y++) {
    const rij = y * width * kanalen
    for (let x = 0; x < width; x++) {
      const { eigen, ander, gewicht } = bron[x]
      for (let k = 0; k < kanalen; k++) {
        const a = lees(data[rij + eigen * kanalen + k])
        const waarde = ander === null ? a : a * (1 - gewicht) + lees(data[rij + ander * kanalen + k]) * gewicht
        nieuw[rij + x * kanalen + k] = schrijf(waarde)
      }
    }
  }
  data.set(nieuw)
  hdri.needsUpdate = true
}

// Hoeveel meter van het model één herhaling van de texture beslaat (de onderdorpel-glb's zijn in meters).
const TEXTURE_TEGEL = 0.5

export function getRvsMaterial (THREE) {
  if (rvsMaterial) return rvsMaterial

  rvsMaterial = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color('#ffffff'),
    map: laadMetaalTexture(THREE, 'Metal041A_1K-JPG_Color.jpg', true),
    // volledig metaal: de kleur en glans komen uit de textures en de reflecties
    metalness: 1,
    // de ruwheid komt uit de texture (vrij donker, ~0.2); roughness vermenigvuldigt die waarde. 0.85 geeft ~0.17:
    // de lichtstroken uit de HDRI blijven herkenbaar maar zijn wat uitgesmeerd, zodat het geen spiegel wordt
    roughness: 0.85,
    roughnessMap: laadMetaalTexture(THREE, 'Metal041A_1K-JPG_Roughness.jpg', false),
    // metaal zonder iets om te weerspiegelen wordt zwart; tot de HDRI geladen is de getekende omgeving
    envMap: maakOmgeving(THREE),
    // iets minder dan 1, zodat de reflecties wat minder hard zijn
    envMapIntensity: 0.85
  })

  new RGBELoader().load(`/textures/${HDRI_BESTAND}`, (hdri) => {
    maakHdriRondom(THREE, hdri, HDRI_LICHTE_KANT_U, HDRI_OVERGANG)
    voegLichtstrokenToe(THREE, hdri, HDRI_LICHTSTROKEN)
    hdri.mapping = THREE.EquirectangularReflectionMapping
    rvsMaterial.envMap = hdri
    rvsMaterial.needsUpdate = true
  }, undefined, (error) => {
    console.warn(`HDRI ${HDRI_BESTAND} kon niet geladen worden, de getekende omgeving blijft staan`, error)
  })

  return rvsMaterial
}

// De onderdorpel-glb's hebben geen UV's (alleen POSITION en NORMAL), waardoor elke texture genegeerd wordt.
// Deze functie maakt ze bij het inladen opnieuw met een box-projectie: elk vlak wordt geprojecteerd langs de as
// waar zijn normaal het meest naar wijst. Over de lengte van de dorpel loopt de texture altijd in de U-richting,
// zodat hij op de voorkant, bovenkant en onderkant netjes doorloopt. Werkt in de eigen assen van de geometrie.
export function genereerRvsUVs (THREE, object) {
  object.traverse((n) => {
    if (!n.isMesh || !n.geometry?.attributes?.position) return
    const geometry = n.geometry
    const position = geometry.attributes.position
    if (!geometry.attributes.normal) geometry.computeVertexNormals()
    const normal = geometry.attributes.normal

    // de lengte van de dorpel is de as met de grootste afmeting
    geometry.computeBoundingBox()
    const maat = geometry.boundingBox.getSize(new THREE.Vector3())
    const assen = ['x', 'y', 'z']
    const lengteAs = assen.reduce((a, b) => (maat[b] > maat[a] ? b : a))

    const uv = new Float32Array(position.count * 2)
    const p = new THREE.Vector3()
    const nn = new THREE.Vector3()
    for (let i = 0; i < position.count; i++) {
      p.fromBufferAttribute(position, i)
      nn.fromBufferAttribute(normal, i)
      const normaalAs = assen.reduce((a, b) => (Math.abs(nn[b]) > Math.abs(nn[a]) ? b : a))

      let u, v
      if (normaalAs === lengteAs) {
        // kopse kant: de twee assen die over blijven
        const [a, b] = assen.filter((as) => as !== lengteAs)
        u = p[a]
        v = p[b]
      } else {
        // voor-, achter-, boven- of onderkant: U langs de lengte, V langs de overgebleven as
        const andere = assen.find((as) => as !== lengteAs && as !== normaalAs)
        u = p[lengteAs]
        v = p[andere]
      }
      uv[i * 2] = u / TEXTURE_TEGEL
      uv[i * 2 + 1] = v / TEXTURE_TEGEL
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  })
}

// De getekende rvs-texture en borstelstructuur worden op dit moment niet gebruikt, maar blijven bewust bewaard
// voor als de echte texture-set niet bevalt.
// eslint-disable-next-line no-unused-vars
const bewaardeGenerators = { maakRvsTexture, maakBorstelStructuur }
